import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { fileUrl, gbp } from "@/lib/api";
import { addToTray, clearTray, removeFromTray, useCompareTray } from "@/lib/compareTray";
import { X, ArrowUpRight } from "lucide-react";

function Slot({ car, idx, onDropCar }) {
  const [over, setOver] = useState(false);
  const label = idx === 0 ? "A" : "B";
  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); onDropCar(e, idx); }}
      data-testid={`compare-slot-${label.toLowerCase()}`}
      className={`flex-1 min-w-0 h-14 sm:h-16 rounded-xl border flex items-center gap-2 px-2 transition-colors ${over ? "border-cyan-300 bg-cyan-300/10" : car ? "border-white/15 bg-white/5" : "border-dashed border-white/25"}`}>
      <span className={`w-6 h-6 rounded-full text-[11px] font-bold flex items-center justify-center shrink-0 ${idx === 0 ? "bg-cyan-400/20 text-cyan-200" : "bg-violet-400/20 text-violet-200"}`}>{label}</span>
      {car ? (
        <>
          {car.photo && <img src={fileUrl(car.photo)} alt="" className="hidden sm:block w-14 h-10 rounded-md object-cover" />}
          <div className="min-w-0 flex-1">
            <p className="text-xs sm:text-sm font-semibold text-white truncate">{car.year} {car.make} {car.model}</p>
            <p className="text-[11px] text-white/60">{gbp(car.price)}</p>
          </div>
          <button onClick={() => removeFromTray(car.id)} className="p-1 text-white/60 hover:text-white" aria-label="Remove" data-testid={`compare-remove-${label.toLowerCase()}`}><X className="w-4 h-4" /></button>
        </>
      ) : <span className="text-xs text-white/50 truncate">Drop a car here</span>}
    </div>
  );
}

export function CompareDock() {
  const items = useCompareTray();
  const { pathname } = useLocation();
  const [dragging, setDragging] = useState(false);
  useEffect(() => {
    const on = (e) => setDragging(!!e.detail);
    window.addEventListener("vorra-drag", on);
    return () => window.removeEventListener("vorra-drag", on);
  }, []);
  const onDropCar = (e, idx) => {
    try { addToTray(JSON.parse(e.dataTransfer.getData("application/x-vorra-car")), idx); } catch { /* not a car */ }
  };
  const show = pathname !== "/compare" && (items.length > 0 || dragging);
  return (
    <AnimatePresence>
      {show && (
        <motion.div initial={{ y: 120, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 120, opacity: 0 }} transition={{ type: "spring", stiffness: 260, damping: 28 }}
          className="fixed bottom-3 inset-x-3 sm:inset-x-auto sm:left-1/2 sm:-translate-x-1/2 sm:w-[720px] z-50 rounded-2xl bg-[#0B1620]/85 backdrop-blur-xl border border-white/10 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.6)] p-2.5 sm:p-3"
          data-testid="compare-dock">
          <div className="flex items-center gap-2">
            <Slot car={items[0]} idx={0} onDropCar={onDropCar} />
            <Slot car={items[1]} idx={1} onDropCar={onDropCar} />
          </div>
          <div className="flex items-center justify-between mt-2 px-1">
            <button onClick={clearTray} className="text-xs text-white/60 hover:text-white" data-testid="compare-clear">Clear</button>
            {items.length === 2 ? (
              <Link to={`/compare?a=${items[0].id}&b=${items[1].id}`} data-testid="compare-open" className="h-8 px-4 rounded-full bg-cyan-400 text-[#06202A] text-xs font-bold inline-flex items-center gap-1 hover:bg-cyan-300 transition-colors">Open hologram <ArrowUpRight className="w-3.5 h-3.5" /></Link>
            ) : <span className="text-[11px] text-white/50">Add 2 cars to compare</span>}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
